import { useContext, useMemo, useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  Table,
  Loader,
  SvgIcon,
  IColumn,
  LabelWithIcon,
  DashboardGraph,
  IDashboardLoginActivities,
} from '@ascd/witsby-components';
import { Box, Grid, Tooltip, IconButton } from '@mui/material';
import * as FileSaver from 'file-saver';
import { get, isEmpty, startCase } from 'lodash';
import { DateTime } from 'luxon';
import * as XLSX from 'xlsx';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, IDetailsPage, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_LOGIN_ACTIVITY_FOR_USER from '@graphql/schema/dashboardLoginActivityForUser.graphql';
import { ITableFilter } from '@types';
import { dateFormat, handleGraphqlError, showToast } from '@utils';
import AdminDashboardCommonCard from '../CommonCard';

const LoginActivityUserDetails = () => {
  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { detailsPage, sideBarApiFilter },
  } = useContext(DashboardContext);

  const {
    assignedBy,
    internalId1,
    selectedGroup,
    selectedInstitution,
    createdByOrganizationId,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  const [filter, setFilter] = useState<ITableFilter>({
    page: 0,
    limit: 20,
    searchText: '',
    sortDirection: 'DESC',
    sortField: 'createdAt',
    searchField: [],
  });

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const { detailsPageData } = detailsPage as IDetailsPage;
  const selectedData = detailsPageData?.data as IDashboardLoginActivities;

  const variables = {
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ALL' ||
      currentSelectedInstitution?.label === 'ASCD'
        ? null
        : internalId1.map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
    filter,
    userId: selectedData?._id,
  };

  const { loading, data } = useQuery(DASHBOARD_LOGIN_ACTIVITY_FOR_USER, {
    variables,
    fetchPolicy: 'network-only',
  });

  const dashboardLoginActivityForUser = get(data, 'dashboardLoginActivityForUser', {
    data: [],
    datasets: [],
    labels: [],
    loginActivities: [],
    totalCount: 0,
  });

  const history = get(dashboardLoginActivityForUser, 'loginActivities[0].history', []);

  const loginActivityColumn: IColumn[] = [
    {
      key: 'createdAt',
      name: 'Date/Time',
      width: '80%',
      render: (_, value) => (
        <Box
          sx={{
            fontSize: (theme) => theme.font.size.SMALL,
          }}>
          {DateTime.fromJSDate(
            new Date((value as IDashboardLoginActivities)?.createdAt as string),
          ).toFormat(`''LL'/'dd'/'yyyy'' hh:mm:ss a`)}
        </Box>
      ),
      isSorting: true,
      ...(filter.sortField === 'createdAt' && {
        sortType: filter.sortDirection,
      }),
    },
    {
      key: 'type',
      name: 'Type',
      render: (_, value) => (
        <Box>
          {startCase(((value as IDashboardLoginActivities)?.type as string)?.toLowerCase())}
        </Box>
      ),
    },
  ];

  const [loginActivityForUserCSV, { loading: isCSVLoading }] = useLazyQuery(
    DASHBOARD_LOGIN_ACTIVITY_FOR_USER,
    {
      fetchPolicy: 'network-only',
    },
  );

  const exportToCSV = async () => {
    if (!isCSVLoading) {
      loginActivityForUserCSV({
        variables: {
          ...variables,
          filter: {
            ...filter,
            limit: dashboardLoginActivityForUser.totalCount,
            page: 0,
          },
        },
      })
        .then((res) => {
          const datalist = res.data.dashboardLoginActivityForUser.loginActivities[0].history.map(
            (item: IDashboardLoginActivities) => ({
              'Date/Time': DateTime.fromJSDate(new Date(item?.createdAt as string)).toFormat(
                `''LL'/'dd'/'yyyy'' hh:mm:ss a`,
              ),
              Type: startCase((item?.type as string)?.toLowerCase()),
            }),
          );
          const fileType =
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
          const fileExtension = '.xlsx';
          const ws = XLSX.utils.json_to_sheet(datalist);
          const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
          const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const excelData = new Blob([excelBuffer], { type: fileType });
          FileSaver.saveAs(
            excelData,
            `Login Activity-${selectedData?.userName} (${dateFormat({
              date: startDate,
              format: 'dd-MMM-yyyy',
            })} - ${dateFormat({ date: endDate, format: 'dd-MMM-yyyy' })}) ${fileExtension}`,
          );
          showToast('Downloaded Successfully');
        })
        .catch(handleGraphqlError);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            <AdminDashboardCommonCard
              minHeight={350}
              maxHeight={250}
              title={`Login Activity: ${selectedData?.userName}`}>
              <Box>
                {loading ? (
                  <Box
                    sx={{
                      minHeight: 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Loader />
                  </Box>
                ) : (
                  <DashboardGraph
                    labels={dashboardLoginActivityForUser.labels ?? []}
                    datasets={dashboardLoginActivityForUser.datasets ?? []}
                  />
                )}
              </Box>
            </AdminDashboardCommonCard>
            <Box sx={{ my: '2rem' }}>
              <Table
                data={history}
                loading={loading}
                columns={loginActivityColumn}
                apiPaginationEnable
                count={dashboardLoginActivityForUser.totalCount}
                page={filter.page}
                pageSize={filter.limit}
                activeSortField={filter.sortField}
                handlePageChange={(_, newPage) => {
                  setFilter({ ...filter, page: newPage });
                }}
                handleRowsPerPageChange={(e) => {
                  setFilter({ ...filter, page: 0, limit: parseInt(e.target.value, 10) });
                }}
                onSortClick={(key, sortType) => {
                  setFilter({
                    ...filter,
                    page: 0,
                    sortField: key as string,
                    sortDirection: sortType,
                  });
                }}
                rowsPerPageOptions={[10, 20, 50, 100, 150]}
                aboveTableHeaderChildren={
                  <Box sx={{ p: 2, pt: 0, fontSize: (theme) => theme.font.size.SMALL }}>
                    Times are based on your location.{' '}
                    <b>Time zone: {DateTime.local().toFormat('ZZZZ')}</b>
                  </Box>
                }
                headerChildren={
                  <Box>
                    <LabelWithIcon
                      title={`Login Activity: ${selectedData?.userName}`}
                      iconName="MOVE_UP"
                      iconDirection="left"
                      iconColorHex="#FFFFFF"
                      sx={{ color: '#FFFFFF' }}
                      onClick={() => {
                        dispatch({
                          type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
                          data: {
                            type: 'LOGIN_ACTIVITY',
                            detailsPageData: {},
                          },
                        });
                      }}
                      IconTitleDistance={1}
                      iconSx={{ transform: 'rotate(270deg)' }}
                    />
                  </Box>
                }
                dataEmptyChildren={<Box>No login activity found.</Box>}
                headerExtra={
                  dashboardLoginActivityForUser.totalCount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {isCSVLoading ? (
                        <Loader
                          size={20}
                          sx={{ color: '#FFFFFF', ml: (theme) => theme.spaces.LARGE }}
                        />
                      ) : (
                        <Tooltip describeChild title="Download the report below" arrow>
                          <Box
                            sx={{
                              ml: (theme) => theme.spaces.REGULAR,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                            }}>
                            <IconButton onClick={() => exportToCSV()} aria-label="Handle course">
                              <SvgIcon
                                icon="FILE_DOWNLOAD"
                                viewBox="0 0 22 29"
                                sx={{ fill: '#FFFF' }}
                              />
                            </IconButton>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  )
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LoginActivityUserDetails;
